import { EsusEnvioStatus } from '../../envios/core/_models'

/**
 * Contrato do envio manual da Ficha de Cadastro Individual (FCI) por paciente (F7b).
 *
 * Espelha os records do backend `EsusCadastroContextoDto`, `EsusCadastroLotacaoDto`,
 * `EsusCadastroUnidadeDto`, `EsusCadastroEnvioDto` e `EsusCadastroEnvioResultadoDto`, servidos por
 * `GET`/`POST /pacientes/{id}/esus-cadastro`.
 *
 * A empresa **nunca** trafega daqui: o servidor a deriva do próprio paciente e escopa a resposta
 * pelo usuário autenticado (`hasPermission(#id, 'Paciente', 'Esus:cadastro')`).
 */

/** Unidade da lotação. O `cnes` entra no rótulo porque as lotações podem ser de estabelecimentos diferentes. */
export type EsusCadastroUnidade = {
  id?: number | null
  nome?: string | null
  cnes?: string | null
}

/** CBO da lotação (`EsusEncerramentoCboDto`, reusado pelo backend). */
export type EsusCadastroCbo = {
  codigo?: string | null
  descricao?: string | null
}

/**
 * Lotação elegível para assinar a ficha.
 *
 * Ao contrário do contexto de encerramento (E5c), **não** existe `ultimaUsada` aqui — o DTO da F7b
 * não tem esse campo e não há pré-seleção de "última usada" a inventar. `esusAtivo = false` significa
 * integração e-SUS desligada na unidade: mandar essa lotação devolve 422 previsível.
 */
export type EsusCadastroLotacao = {
  id: number
  unidade?: EsusCadastroUnidade | null
  cbo?: EsusCadastroCbo | null
  ine?: string | null
  esusAtivo: boolean
}

/** `GET /pacientes/{id}/esus-cadastro` — 200 com lista vazia quando não há lotação elegível. */
export type EsusCadastroContexto = {
  lotacoes: EsusCadastroLotacao[]
}

/**
 * Desfechos que chegam ao cliente em 2xx.
 *
 * O enum do backend tem cinco valores, mas `PACIENTE_INVALIDO`, `LOTACAO_INVALIDA` e
 * `UNIDADE_SEM_CNES` viram **422** (`DesfechoCadastroEsus.isRecusa()`) e nunca aparecem no corpo de
 * sucesso.
 */
export type DesfechoCadastroEsus = 'CRIADO' | 'SEM_ALTERACAO'

/** `POST /pacientes/{id}/esus-cadastro` — resultado em 2xx. */
export type EsusCadastroResultado = {
  desfecho: DesfechoCadastroEsus
  mensagem?: string | null
  envioId?: number | null
  /** Mesmo enum da fila de envios: reusa `rotuloStatus` para o texto em pt-BR. */
  status?: EsusEnvioStatus | null
  uuidFicha?: string | null
  mensagemErro?: string | null
}

/**
 * Rótulo da lotação no seletor: `CBO - descrição · Unidade (CNES …)` e, quando houver, `(INE …)`.
 *
 * Estende o rótulo da E5c com a unidade de propósito: lá as lotações eram todas do mesmo
 * atendimento; aqui podem ser de estabelecimentos diferentes, e é o CNES que identifica para qual
 * PEC a ficha vai.
 */
export const rotuloLotacaoCadastro = (lotacao: EsusCadastroLotacao): string => {
  const cbo = lotacao.cbo
  const partes = [
    cbo ? [cbo.codigo, cbo.descricao].filter(Boolean).join(' - ') : `Lotação ${lotacao.id}`,
  ]

  const unidade = lotacao.unidade
  if (unidade?.nome) {
    partes.push(unidade.cnes ? `${unidade.nome} (CNES ${unidade.cnes})` : unidade.nome)
  }

  const base = partes.filter(Boolean).join(' · ')
  return lotacao.ine ? `${base} (INE ${lotacao.ine})` : base
}
